import type {
  ContractGenerationTemplateFieldInDb,
  ContractGenerationTemplateFieldsResponse,
  TemplateField,
} from '@/api';
import { hasAuthoredProse, humanizeKey, lastPathSegment } from './templateFieldLabels';
import { flattenFields, type FlattenedField } from './templateSchemaFields';

/**
 * One field of the guidance page: the schema's structural facts (when the
 * schema is readable) beside the server's authored row (when one exists),
 * joined by path. Every row is keyed by `path` — unsaved rows all share the
 * backend's zero uid, so the uid identifies nothing.
 */
export interface FieldTreeRow {
  path: string;
  /** `''` for a root field; the list's `[]` marker for list-item children. */
  parentPath: string;
  depth: number;
  /** What the wizard shows when no label is authored — the humanized key. */
  fallbackLabel: string;
  schema: FlattenedField | null;
  server: ContractGenerationTemplateFieldInDb | null;
  hasChildren: boolean;
  /** The `parent_path` this row's children carry: the path, or `path[]` for a list. */
  childrenKey: string;
}

export interface FieldTreeModel {
  /** Pre-order, siblings in resolved order. */
  rows: FieldTreeRow[];
  byPath: Map<string, FieldTreeRow>;
  /** Rows whose tag is gone and which carry writing worth moving. */
  orphans: ContractGenerationTemplateFieldInDb[];
  /** False while the template is re-extracting or its last extraction failed. */
  schemaAvailable: boolean;
}

/**
 * A row's parent, by the backend's own rule (`parent_path_of`): the path up to
 * its last dot — `services[].fee` → `services[]`, `client.address.city` →
 * `client.address` — and `''` for a root field. Derived here rather than read
 * off the row: the backend no longer stores it, and the tree must not depend
 * on the API continuing to send it.
 */
function parentPathOf(path: string): string {
  const dot = path.lastIndexOf('.');
  return dot === -1 ? '' : path.slice(0, dot);
}

function rowsFromSchema(
  schemaFields: Record<string, TemplateField>,
  serverByPath: Map<string, ContractGenerationTemplateFieldInDb>
): FieldTreeRow[] {
  return flattenFields(schemaFields).map(flat => ({
    path: flat.path,
    parentPath: parentPathOf(flat.path),
    depth: flat.depth,
    fallbackLabel: humanizeKey(flat.key),
    schema: flat,
    server: serverByPath.get(flat.path) ?? null,
    hasChildren: flat.hasChildren,
    childrenKey: flat.isCollection === 'list' ? `${flat.path}[]` : flat.path,
  }));
}

/**
 * Without a schema the server's rows are all we have: their paths place them,
 * so the tree can still be drawn — depth walks the parent chain, and a list is
 * recognised by children hanging off its `[]` marker.
 */
function rowsFromServer(fields: ContractGenerationTemplateFieldInDb[]): FieldTreeRow[] {
  const parentKeys = new Set(fields.map(field => parentPathOf(field.path)));
  const depthByPath = new Map<string, number>();
  function depthOf(path: string): number {
    const known = depthByPath.get(path);
    if (known !== undefined) return known;
    const parentPath = parentPathOf(path);
    if (!parentPath) {
      depthByPath.set(path, 0);
      return 0;
    }
    const parent = fields.find(field => field.path === parentPath.replace(/\[\]$/, ''));
    const depth = parent ? depthOf(parent.path) + 1 : parentPath.split('.').length;
    depthByPath.set(path, depth);
    return depth;
  }
  return fields.map(field => {
    const isList = parentKeys.has(`${field.path}[]`);
    return {
      path: field.path,
      parentPath: parentPathOf(field.path),
      depth: depthOf(field.path),
      fallbackLabel: humanizeKey(lastPathSegment(field.path)),
      schema: null,
      server: field,
      hasChildren: isList || parentKeys.has(field.path),
      childrenKey: isList ? `${field.path}[]` : field.path,
    };
  });
}

/**
 * Join the schema (structure, document order) with the authored rows and
 * arrange every sibling group the way the backend resolves it — arranged
 * fields first by their position, the rest in document order — with the
 * author's unsaved positions overlaid so a drop reorders the tree at once and
 * the order after saving is the one they saw.
 */
export function buildFieldTree(
  schemaFields: Record<string, TemplateField> | null | undefined,
  response: ContractGenerationTemplateFieldsResponse | null,
  draftOrder: (path: string) => number | null
): FieldTreeModel {
  const serverByPath = new Map<string, ContractGenerationTemplateFieldInDb>();
  for (const field of response?.fields ?? []) serverByPath.set(field.path, field);

  const schemaAvailable = Boolean(schemaFields && Object.keys(schemaFields).length > 0);
  const unsorted = schemaAvailable
    ? rowsFromSchema(schemaFields ?? {}, serverByPath)
    : rowsFromServer(response?.fields ?? []);

  const documentIndex = new Map<string, number>();
  for (const [index, row] of unsorted.entries()) documentIndex.set(row.path, index);

  function sortKey(row: FieldTreeRow): [number, number, number] {
    const order = draftOrder(row.path);
    return [
      order === null ? 1 : 0,
      order ?? 0,
      row.server?.sort_position ?? documentIndex.get(row.path) ?? 0,
    ];
  }

  const childrenByParent = new Map<string, FieldTreeRow[]>();
  for (const row of unsorted) {
    const siblings = childrenByParent.get(row.parentPath) ?? [];
    siblings.push(row);
    childrenByParent.set(row.parentPath, siblings);
  }
  for (const siblings of childrenByParent.values()) {
    siblings.sort((a, b) => {
      const [flagA, orderA, positionA] = sortKey(a);
      const [flagB, orderB, positionB] = sortKey(b);
      return (
        flagA - flagB ||
        orderA - orderB ||
        positionA - positionB ||
        (documentIndex.get(a.path) ?? 0) - (documentIndex.get(b.path) ?? 0)
      );
    });
  }

  const rows: FieldTreeRow[] = [];
  const emitted = new Set<string>();
  function emit(parentKey: string): void {
    for (const row of childrenByParent.get(parentKey) ?? []) {
      if (emitted.has(row.path)) continue;
      emitted.add(row.path);
      rows.push(row);
      emit(row.childrenKey);
    }
  }
  emit('');
  // Rows whose parent chain is broken (a server row whose parent was renamed
  // mid-extraction) still deserve a place rather than vanishing.
  for (const row of unsorted) {
    if (!emitted.has(row.path)) rows.push(row);
  }

  const byPath = new Map<string, FieldTreeRow>();
  for (const row of rows) byPath.set(row.path, row);

  return {
    rows,
    byPath,
    orphans: (response?.orphaned ?? []).filter(hasAuthoredProse),
    schemaAvailable,
  };
}
